import { useState, useEffect } from 'react';
import {
  Link2, RefreshCw, Bell, Settings2, ChevronDown, ChevronRight,
  SkipForward, RotateCcw, AlertCircle, CheckCircle, Loader2, AlertTriangle,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { userApi, flightApi, type LinkSettings, type UnlinkedLeg, type AutoLinkLogEntry } from '@/services/api';
import { useToast } from '@/components/ui/use-toast';
import { format, parseISO } from 'date-fns';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function LegRow({
  leg,
  onSkip,
  onUnskip,
  showUnskip,
}: {
  leg: UnlinkedLeg;
  onSkip?: (id: number, reason: string) => Promise<void>;
  onUnskip?: (id: number) => Promise<void>;
  showUnskip?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const handleSkip = async () => {
    setBusy(true);
    await onSkip?.(leg.id, reason);
    setBusy(false);
    setExpanded(false);
    setReason('');
  };

  const handleUnskip = async () => {
    setBusy(true);
    await onUnskip?.(leg.id);
    setBusy(false);
  };

  return (
    <div className="border-b last:border-0 py-2.5 px-1">
      <div className="flex items-center gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-sm">{leg.flightNo}</span>
            <span className="text-xs text-gray-400">{leg.flightDate}</span>
            <span className="text-xs text-gray-500">
              {leg.depShortName || leg.depCode} → {leg.arrShortName || leg.arrCode}
            </span>
            {leg.postName && (
              <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">
                {leg.postName}
              </span>
            )}
            {leg.linkSkipped && leg.linkSkipReason && (
              <span className="text-xs text-amber-600 italic truncate max-w-[160px]" title={leg.linkSkipReason}>
                原因：{leg.linkSkipReason}
              </span>
            )}
          </div>
        </div>
        {showUnskip ? (
          <Button size="sm" variant="ghost" className="text-xs h-7 shrink-0" onClick={handleUnskip} disabled={busy}>
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <><RotateCcw className="w-3 h-3 mr-1" />恢复</>}
          </Button>
        ) : (
          <Button
            size="sm"
            variant="ghost"
            className="text-xs h-7 text-gray-400 hover:text-amber-600 shrink-0"
            onClick={() => setExpanded(!expanded)}
            disabled={busy}
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <><SkipForward className="w-3 h-3 mr-1" />跳过</>}
          </Button>
        )}
      </div>

      {expanded && (
        <div className="mt-2 flex gap-2 items-start pl-0">
          <textarea
            value={reason}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setReason(e.target.value)}
            placeholder="跳过原因（可选）"
            className="flex-1 min-h-[64px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none"
          />
          <div className="flex flex-col gap-1 shrink-0">
            <Button size="sm" className="h-7 text-xs" onClick={handleSkip} disabled={busy}>确认</Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setExpanded(false)}>取消</Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function AutoLinkSection() {
  const { toast } = useToast();

  // Settings state
  const [settings, setSettings] = useState<LinkSettings | null>(null);
  const [settingsLoading, setSettingsLoading] = useState(true);

  // Unlinked legs
  const [unlinkedLegs, setUnlinkedLegs] = useState<UnlinkedLeg[]>([]);
  const [unlinkedCount, setUnlinkedCount] = useState(0);
  const [unlinkedPage, setUnlinkedPage] = useState(1);
  const [unlinkedTotalPages, setUnlinkedTotalPages] = useState(1);
  const [unlinkedLoading, setUnlinkedLoading] = useState(true);

  // Skipped legs
  const [skippedLegs, setSkippedLegs] = useState<UnlinkedLeg[]>([]);
  const [skippedCount, setSkippedCount] = useState(0);
  const [skippedExpanded, setSkippedExpanded] = useState(false);
  const [skippedLoading, setSkippedLoading] = useState(false);

  // Auto-link action
  const [isLinking, setIsLinking] = useState(false);

  // Logs
  const [logs, setLogs] = useState<AutoLinkLogEntry[]>([]);
  const [logsLoading, setLogsLoading] = useState(true);

  useEffect(() => {
    fetchSettings();
    fetchUnlinkedLegs(1);
    fetchLogs();
  }, []);

  useEffect(() => {
    if (skippedExpanded && skippedLegs.length === 0) {
      fetchSkippedLegs();
    }
  }, [skippedExpanded]);

  const fetchSettings = async () => {
    try {
      const res = await userApi.getLinkSettings();
      setSettings(res.data.data);
    } catch {
      // silent
    } finally {
      setSettingsLoading(false);
    }
  };

  const fetchUnlinkedLegs = async (page: number) => {
    setUnlinkedLoading(true);
    try {
      const res = await flightApi.getUnlinkedLegs({ page, page_size: 10 });
      setUnlinkedLegs(res.data.data.results);
      setUnlinkedCount(res.data.data.count);
      setUnlinkedPage(page);
      setUnlinkedTotalPages(res.data.data.total_pages);
    } catch {
      // silent
    } finally {
      setUnlinkedLoading(false);
    }
  };

  const fetchSkippedLegs = async () => {
    setSkippedLoading(true);
    try {
      const res = await flightApi.getSkippedLegs({ page_size: 50 });
      setSkippedLegs(res.data.data.results);
      setSkippedCount(res.data.data.count);
    } catch {
      // silent
    } finally {
      setSkippedLoading(false);
    }
  };

  const fetchLogs = async () => {
    setLogsLoading(true);
    try {
      const res = await flightApi.getAutoLinkLogs();
      setLogs(res.data.data);
    } catch {
      // silent
    } finally {
      setLogsLoading(false);
    }
  };

  const handleAutoLink = async () => {
    setIsLinking(true);
    try {
      await flightApi.autoLinkLegs();
      toast({ title: '自动关联任务已触发', description: '正在后台处理，2 秒后刷新' });
      setTimeout(() => {
        fetchUnlinkedLegs(1);
        fetchSettings();
        fetchLogs();
      }, 2000);
    } catch {
      toast({ title: '触发失败', variant: 'destructive' });
    } finally {
      setTimeout(() => setIsLinking(false), 2000);
    }
  };

  const handleSkip = async (id: number, reason: string) => {
    try {
      await flightApi.skipLeg(id, reason);
      await fetchUnlinkedLegs(unlinkedPage);
      if (skippedExpanded) await fetchSkippedLegs();
    } catch {
      toast({ title: '操作失败', variant: 'destructive' });
    }
  };

  const handleUnskip = async (id: number) => {
    try {
      await flightApi.unskipLeg(id);
      await fetchSkippedLegs();
      await fetchUnlinkedLegs(1);
    } catch {
      toast({ title: '操作失败', variant: 'destructive' });
    }
  };

  const handleToggle = async (key: keyof LinkSettings, value: boolean | number) => {
    if (!settings) return;
    const prev = { ...settings };
    setSettings({ ...settings, [key]: value });
    try {
      await userApi.updateLinkSettings({ [key]: value });
    } catch {
      setSettings(prev);
      toast({ title: '保存失败', variant: 'destructive' });
    }
  };

  const lastLinkTime = settings?.lastAutoLinkAt
    ? format(parseISO(settings.lastAutoLinkAt), 'MM-dd HH:mm')
    : null;

  return (
    <div className="space-y-4">

      {/* ── 未关联航段列表 ── */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-base">
              <Link2 className="w-4 h-4 text-orange-500" />
              未关联航段
              {unlinkedCount > 0 && (
                <span className="ml-1 text-xs bg-orange-100 text-orange-600 px-1.5 py-0.5 rounded-full">
                  {unlinkedCount}
                </span>
              )}
            </CardTitle>
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs"
              onClick={handleAutoLink}
              disabled={isLinking}
            >
              {isLinking
                ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" />关联中</>
                : <><RefreshCw className="w-3 h-3 mr-1" />立即关联</>}
            </Button>
          </div>
          {lastLinkTime && (
            <p className="text-xs text-gray-400 mt-1">上次关联：{lastLinkTime}，新增 {settings?.lastAutoLinkLinked ?? 0} 条</p>
          )}
        </CardHeader>
        <CardContent className="pt-0">
          {unlinkedLoading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-5 h-5 animate-spin text-gray-300" />
            </div>
          ) : unlinkedLegs.length === 0 ? (
            <div className="text-center py-6 text-sm text-gray-400">
              <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-300" />
              暂无未关联航段
            </div>
          ) : (
            <>
              <div className="divide-y">
                {unlinkedLegs.map((leg) => (
                  <LegRow key={leg.id} leg={leg} onSkip={handleSkip} />
                ))}
              </div>
              {unlinkedTotalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-3">
                  <Button
                    size="sm" variant="ghost" className="h-7 text-xs"
                    disabled={unlinkedPage <= 1}
                    onClick={() => fetchUnlinkedLegs(unlinkedPage - 1)}
                  >上一页</Button>
                  <span className="text-xs text-gray-400">{unlinkedPage} / {unlinkedTotalPages}</span>
                  <Button
                    size="sm" variant="ghost" className="h-7 text-xs"
                    disabled={unlinkedPage >= unlinkedTotalPages}
                    onClick={() => fetchUnlinkedLegs(unlinkedPage + 1)}
                  >下一页</Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* ── 已跳过航段（折叠） ── */}
      <Card>
        <CardHeader
          className="pb-3 cursor-pointer select-none"
          onClick={() => setSkippedExpanded(!skippedExpanded)}
        >
          <CardTitle className="flex items-center gap-2 text-base text-gray-500">
            {skippedExpanded
              ? <ChevronDown className="w-4 h-4" />
              : <ChevronRight className="w-4 h-4" />}
            已跳过航段
            {skippedCount > 0 && (
              <span className="ml-1 text-xs bg-gray-100 text-gray-400 px-1.5 py-0.5 rounded-full">
                {skippedCount}
              </span>
            )}
          </CardTitle>
        </CardHeader>
        {skippedExpanded && (
          <CardContent className="pt-0">
            {skippedLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-5 h-5 animate-spin text-gray-300" />
              </div>
            ) : skippedLegs.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-4">暂无已跳过航段</p>
            ) : (
              <div className="divide-y">
                {skippedLegs.map((leg) => (
                  <LegRow key={leg.id} leg={leg} onUnskip={handleUnskip} showUnskip />
                ))}
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* ── 自动关联日志 ── */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <AlertTriangle className="w-4 h-4 text-amber-500" />
            关联异常日志
          </CardTitle>
          <CardDescription>仅显示失败或存在歧义的运行记录</CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          {logsLoading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-5 h-5 animate-spin text-gray-300" />
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-4 text-sm text-gray-400">
              <CheckCircle className="w-6 h-6 mx-auto mb-1.5 text-green-300" />
              暂无异常记录
            </div>
          ) : (
            <div className="space-y-2">
              {logs.map((log) => (
                <div key={log.id} className={`rounded-lg p-3 text-sm ${
                  log.status === 'failed'
                    ? 'bg-red-50 border border-red-100'
                    : 'bg-amber-50 border border-amber-100'
                }`}>
                  <div className="flex items-center gap-2 mb-1">
                    {log.status === 'failed'
                      ? <AlertCircle className="w-3.5 h-3.5 text-red-500 shrink-0" />
                      : <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0" />}
                    <span className={`text-xs font-medium ${log.status === 'failed' ? 'text-red-600' : 'text-amber-600'}`}>
                      {log.status === 'failed' ? '失败' : '部分完成'}
                    </span>
                    <span className="text-xs text-gray-400 ml-auto">
                      {format(parseISO(log.triggeredAt), 'MM-dd HH:mm')}
                    </span>
                  </div>
                  {log.ambiguousCount > 0 && (
                    <p className="text-xs text-gray-600">歧义跳过：{log.ambiguousCount} 条（同一天同航班号有多条 FSOP 记录）</p>
                  )}
                  {log.errorMessage && (
                    <p className="text-xs text-red-600 mt-1 break-all">{log.errorMessage}</p>
                  )}
                  {log.linkedCount > 0 && (
                    <p className="text-xs text-gray-500 mt-0.5">本次已关联：{log.linkedCount} 条</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── 自动关联设置 ── */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Settings2 className="w-4 h-4 text-indigo-500" />
            自动关联设置
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {settingsLoading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-5 h-5 animate-spin text-gray-300" />
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <Label htmlFor="eflight-auto-link" className="flex flex-col gap-0.5">
                  <span>eFlight 同步后自动关联</span>
                  <span className="font-normal text-xs text-gray-400">每次 eFlight 同步完成后自动触发</span>
                </Label>
                <Switch
                  id="eflight-auto-link"
                  checked={settings?.autoLinkAfterEflightSync ?? true}
                  onCheckedChange={(v) => handleToggle('autoLinkAfterEflightSync', v)}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="fsop-auto-link" className="flex flex-col gap-0.5">
                  <span>FSOP 同步后自动关联</span>
                  <span className="font-normal text-xs text-gray-400">每次 FSOP 同步完成后自动触发</span>
                </Label>
                <Switch
                  id="fsop-auto-link"
                  checked={settings?.autoLinkAfterFsopSync ?? true}
                  onCheckedChange={(v) => handleToggle('autoLinkAfterFsopSync', v)}
                />
              </div>
              <p className="text-xs text-gray-400">
                匹配规则：同一用户 + 同一日期 + 同一航班号。严格匹配失败时降级到日期+航班号，有歧义则跳过。手动关联/取消的航段不受影响。
              </p>
            </>
          )}
        </CardContent>
      </Card>

      {/* ── 超期未关联提醒 ── */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Bell className="w-4 h-4 text-amber-500" />
            超期未关联提醒
          </CardTitle>
          <CardDescription>
            航班执行超过指定天数且未关联 FSOP 记录时，通过 Bark 发送提醒
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {settingsLoading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-5 h-5 animate-spin text-gray-300" />
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <Label htmlFor="unlinked-reminder" className="flex flex-col gap-0.5">
                  <span>启用超期提醒</span>
                  <span className="font-normal text-xs text-gray-400">需在推送设置中配置 Bark Key</span>
                </Label>
                <Switch
                  id="unlinked-reminder"
                  checked={settings?.unlinkedReminderEnabled ?? false}
                  onCheckedChange={(v) => handleToggle('unlinkedReminderEnabled', v)}
                />
              </div>
              {settings?.unlinkedReminderEnabled && (
                <div className="flex items-center gap-3">
                  <Label htmlFor="reminder-days" className="whitespace-nowrap text-sm">提醒阈值</Label>
                  <Input
                    id="reminder-days"
                    type="number"
                    min={30}
                    max={180}
                    value={settings.unlinkedReminderDays}
                    onChange={(e) => {
                      const v = Math.min(180, Math.max(30, Number(e.target.value)));
                      handleToggle('unlinkedReminderDays', v);
                    }}
                    className="w-24"
                  />
                  <span className="text-sm text-gray-500">天</span>
                </div>
              )}
              <div className="rounded-lg bg-blue-50 p-3 flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
                <p className="text-xs text-blue-700">
                  每天最多发送一次，仅提醒 1 年内的航段。已跳过或已关联的航段不会触发提醒。
                </p>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
